import { useState, useRef, useCallback } from "react";
import type { VenueDetail } from "../../../mocks/allVenues";
import { supabase } from "../../../lib/supabase";

const CATEGORIES = [
  { label: "Tarihi Yerler", slug: "historical" },
  { label: "Lezzet Durakları", slug: "food" },
  { label: "Eğlence", slug: "entertainment" },
  { label: "Konaklama", slug: "accommodation" },
  { label: "Doğa ve Rotalar", slug: "nature" },
  { label: "Gezilecek Yerler", slug: "sightseeing" },
];

const DISTRICTS = ["Osmangazi", "Yıldırım", "Nilüfer", "Mudanya", "Karacabey", "Gemlik"];

interface VenueFormModalProps {
  mode: "add" | "edit";
  initial?: Partial<VenueDetail>;
  onSave: (data: Partial<VenueDetail>) => void;
  onClose: () => void;
}

export default function VenueFormModal({ mode, initial = {}, onSave, onClose }: VenueFormModalProps) {
  const [form, setForm] = useState({
    name: initial.name ?? "",
    categorySlug: initial.categorySlug ?? "historical",
    category: initial.category ?? "Tarihi Yerler",
    district: initial.district ?? "Osmangazi",
    address: initial.address ?? "",
    phone: initial.phone ?? "",
    description: initial.description ?? "",
    rating: initial.rating ?? 4.5,
    reviews: initial.reviews ?? 0,
    lat: (initial as Record<string, unknown>).lat as number ?? 40.1826,
    lng: (initial as Record<string, unknown>).lng as number ?? 29.0665,
    tags: (initial.tags ?? []).join(", "),
    isSponsored: initial.isSponsored ?? false,
    isEditorPick: initial.isEditorPick ?? false,
    isOpen: initial.isOpen ?? true,
    image: initial.image ?? "",
  });

  const [imagePreview, setImagePreview] = useState<string>(initial.image ?? "");
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCategoryChange = (slug: string) => {
    const cat = CATEGORIES.find((c) => c.slug === slug);
    setForm((f) => ({ ...f, categorySlug: slug, category: cat?.label ?? "" }));
  };

  const uploadImage = useCallback(async (file: File) => {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      setImageError("Sadece JPG, PNG veya WEBP yüklenebilir.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError("Dosya 5MB'dan büyük olamaz.");
      return;
    }

    setImageError("");
    setImageUploading(true);

    // Show local preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);

    try {
      const ext = file.type.split("/")[1].replace("jpeg", "jpg");
      const fileName = `venue-${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const filePath = `venues/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("venue-images")
        .upload(filePath, file, { upsert: false, contentType: file.type });

      if (uploadError) {
        // Try avatars bucket as fallback
        const { error: fallbackError } = await supabase.storage
          .from("avatars")
          .upload(filePath, file, { upsert: false, contentType: file.type });

        if (fallbackError) {
          setImageError("Görsel yüklenemedi: " + fallbackError.message);
          setImageUploading(false);
          return;
        }

        const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(filePath);
        const url = `${urlData.publicUrl}?v=${Date.now()}`;
        setForm((f) => ({ ...f, image: url }));
        setImagePreview(url);
      } else {
        const { data: urlData } = supabase.storage.from("venue-images").getPublicUrl(filePath);
        const url = `${urlData.publicUrl}?v=${Date.now()}`;
        setForm((f) => ({ ...f, image: url }));
        setImagePreview(url);
      }
    } catch {
      setImageError("Görsel yüklenirken hata oluştu.");
    }

    setImageUploading(false);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadImage(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadImage(file);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    onSave({
      ...form,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      rating: Number(form.rating),
      reviews: Number(form.reviews),
    });
  };

  const inputCls = "w-full px-4 py-2.5 border border-stone-200 rounded-xl text-sm outline-none focus:border-emerald-400 transition-colors";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-stone-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="font-bold text-stone-800">
            {mode === "add" ? "Yeni Mekan Ekle" : "Mekanı Düzenle"}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Image Upload */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-2">Mekan Görseli</label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => !imageUploading && fileInputRef.current?.click()}
              className={`relative w-full h-44 rounded-xl border-2 border-dashed transition-all cursor-pointer overflow-hidden ${
                dragOver
                  ? "border-emerald-400 bg-emerald-50"
                  : imagePreview
                  ? "border-stone-200"
                  : "border-stone-200 hover:border-emerald-300 bg-stone-50 hover:bg-emerald-50/30"
              }`}
            >
              {imagePreview ? (
                <>
                  <img
                    src={imagePreview}
                    alt="Önizleme"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="text-white text-center">
                      <i className="ri-image-edit-line text-2xl block mb-1" />
                      <span className="text-sm font-medium">Görseli Değiştir</span>
                    </div>
                  </div>
                  {imageUploading && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="flex flex-col items-center gap-2 text-white">
                        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm">Yükleniyor...</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center h-full gap-2 text-stone-400">
                  {imageUploading ? (
                    <>
                      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm text-emerald-600">Yükleniyor...</span>
                    </>
                  ) : (
                    <>
                      <i className="ri-image-add-line text-3xl" />
                      <div className="text-center">
                        <p className="text-sm font-medium text-stone-600">Görsel yükle veya sürükle</p>
                        <p className="text-xs text-stone-400 mt-0.5">JPG, PNG, WEBP · Maks. 5MB</p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
            {imageError && (
              <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
                <i className="ri-error-warning-line" /> {imageError}
              </p>
            )}
            {imagePreview && !imageUploading && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setImagePreview(""); setForm((f) => ({ ...f, image: "" })); }}
                className="mt-1.5 text-xs text-red-500 hover:text-red-700 cursor-pointer flex items-center gap-1"
              >
                <i className="ri-delete-bin-line" /> Görseli Kaldır
              </button>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Mekan Adı *</label>
            <input
              required
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Mekan adını girin"
              className={inputCls}
            />
          </div>

          {/* Category + District */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Kategori *</label>
              <select
                value={form.categorySlug}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className={`${inputCls} cursor-pointer text-stone-700`}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">İlçe *</label>
              <select
                value={form.district}
                onChange={(e) => setForm((f) => ({ ...f, district: e.target.value }))}
                className={`${inputCls} cursor-pointer text-stone-700`}
              >
                {DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Adres</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              placeholder="Tam adres"
              className={inputCls}
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Telefon</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              placeholder="+90 224 000 0000"
              className={inputCls}
            />
          </div>

          {/* Lat / Lng */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Enlem (Latitude)</label>
              <input
                type="number"
                step="0.000001"
                value={form.lat}
                onChange={(e) => setForm((f) => ({ ...f, lat: parseFloat(e.target.value) }))}
                placeholder="40.1826"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Boylam (Longitude)</label>
              <input
                type="number"
                step="0.000001"
                value={form.lng}
                onChange={(e) => setForm((f) => ({ ...f, lng: parseFloat(e.target.value) }))}
                placeholder="29.0665"
                className={inputCls}
              />
            </div>
          </div>
          <p className="text-xs text-stone-400 -mt-2">
            <i className="ri-map-pin-line mr-1" />
            Kullanıcı konumuna göre uzaklık otomatik hesaplanır.
          </p>

          {/* Rating + Reviews */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Puan (0–5)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={form.rating}
                onChange={(e) => setForm((f) => ({ ...f, rating: parseFloat(e.target.value) }))}
                className={inputCls}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Yorum Sayısı</label>
              <input
                type="number"
                min="0"
                value={form.reviews}
                onChange={(e) => setForm((f) => ({ ...f, reviews: parseInt(e.target.value) }))}
                className={inputCls}
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Açıklama</label>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Mekan açıklaması..."
              maxLength={500}
              className={`${inputCls} resize-none`}
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-xs font-medium text-stone-600 mb-1">Etiketler (virgülle ayırın)</label>
            <input
              type="text"
              value={form.tags}
              onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
              placeholder="Tarihi, Cami, UNESCO, Ücretsiz"
              className={inputCls}
            />
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-4">
            {[
              { key: "isOpen", label: "Açık" },
              { key: "isSponsored", label: "Sponsorlu" },
              { key: "isEditorPick", label: "Editör Seçimi" },
            ].map((toggle) => (
              <label key={toggle.key} className="flex items-center gap-2 cursor-pointer select-none">
                <div
                  onClick={() => setForm((f) => ({ ...f, [toggle.key]: !f[toggle.key as keyof typeof f] }))}
                  className={`w-10 h-5 rounded-full transition-colors relative ${
                    form[toggle.key as keyof typeof form] ? "bg-emerald-600" : "bg-stone-200"
                  }`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                    form[toggle.key as keyof typeof form] ? "translate-x-5" : "translate-x-0.5"
                  }`} />
                </div>
                <span className="text-sm text-stone-700">{toggle.label}</span>
              </label>
            ))}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 border border-stone-200 rounded-xl text-sm text-stone-600 hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={imageUploading}
              className="flex-1 py-2.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              {mode === "add" ? "Mekan Ekle" : "Değişiklikleri Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
